import { Box, Card, CardActionArea, Container } from '@mui/material';
import Image from 'next/image';
import NextLink from 'next/link';
import React from 'react';

import { getImageUrl } from '@/lib/helpers/getImageUrl';
import { UploadFile } from '@/types';

type Props = {
  media: UploadFile;
  href?: string;
  borderRadius?: number;
  aspectRatio?: string; // pass for carousel
  fit?: 'cover' | 'contain'; // defaults cover
  width?: number;
  height?: number;
};

export function MediaCard({
  media,
  href,
  borderRadius = 3,
  aspectRatio,
  fit = 'cover',
  width,
  height,
}: Props) {
  const mediaUrl = getImageUrl({ image: media, size: 'url' });
  if (!mediaUrl) return null;

  const isVideo = media.mime?.startsWith('video');

  const imgWidth = width && width > 0 ? width : (media.width ?? 1200);
  const imgHeight = height && height > 0 ? height : (media.height ?? 800);

  const content = (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        ...(aspectRatio ? { aspectRatio } : { display: 'flex', justifyContent: 'center' }),
        borderRadius,
        overflow: 'hidden',
      }}
    >
      {isVideo ? (
        <video
          src={mediaUrl}
          autoPlay
          muted
          loop
          playsInline
          style={{
            width: '100%',
            height: '100%',
            objectFit: fit,
            display: 'block',
          }}
        />
      ) : aspectRatio ? (
        // Carousel version (fill + enforced aspect ratio)
        <Image
          src={mediaUrl}
          alt={media.alternativeText ?? 'Media'}
          fill
          style={{ objectFit: fit }}
          sizes="(max-width: 600px) 100vw, 33vw"
        />
      ) : (
        // Single image version (natural height)
        <Image
          src={mediaUrl}
          alt={media.alternativeText ?? 'Media'}
          width={imgWidth}
          height={imgHeight}
          style={{
            maxWidth: '100%',
            height: 'auto',
            objectFit: fit,
            display: 'block',
          }}
        />
      )}
    </Box>
  );

  return (
    <Container maxWidth="xl">
      <Card elevation={0} sx={{ borderRadius, overflow: 'hidden', p: 0 }}>
        {href ? (
          <CardActionArea component={NextLink} href={href}>
            {content}
          </CardActionArea>
        ) : (
          content
        )}
      </Card>
    </Container>
  );
}
